import { useState, type FormEvent } from 'react';
import { PlugZap, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@ncoder/ui';
import { useConfirmModal } from '@/hooks/useConfirmModal';
import { IconActionButton } from '@/components/molecules/IconActionButton';
import { FormField, ORG_ADMIN_INPUT_CLASS } from '@/components/molecules/org-admin/OrgAdminForm';
import { OrgAdminEmptyState, OrgAdminErrorState } from '@/components/molecules/org-admin/OrgAdminStates';
import { useCreateMcpServer, useDeleteMcpServer, useMcpServers } from '@/hooks/tools';
import type { McpServer, McpServerAuthMode } from '@/services/mcp-gateway';

/**
 * Error codes the gateway's `/api/mcp/servers` endpoints return as `{detail: "<code>"}`. The axios
 * response interceptor (`@/dataProvider/axios`) already rewrites `error.message` to this raw detail
 * string for any code it does not itself know, so `gatewayErrorMessage` only has to translate it.
 */
const ERROR_COPY: Record<string, string> = {
  org_admin_required: 'Only org admins can manage MCP servers.',
  mcp_url_must_be_https: 'The server URL must start with https://.',
  mcp_url_not_public: 'That URL points to a private or internal address.',
  mcp_url_unresolvable: "That server's hostname could not be resolved.",
  mcp_url_host_missing: 'Enter a valid server URL.',
  mcp_server_not_found: 'That server was already removed — refresh the list.',
  mcp_server_requires_dynamic_client_registration:
    "This server doesn't support automatic sign-in. Choose API token instead.",
  mcp_oauth_endpoints_missing: "This server's OAuth metadata is invalid or missing.",
  mcp_oauth_upstream_error: "Couldn't reach this server's sign-in endpoints.",
  mcp_url_has_credentials: 'Remove the username or password from the URL.',
};

function gatewayErrorMessage(err: unknown): string {
  const message = (err as Error | undefined)?.message;
  return (message && ERROR_COPY[message]) || message || 'Request failed';
}

export function McpServersPanel() {
  const { data: servers = [], isLoading, isError, error, refetch } = useMcpServers();
  const createServer = useCreateMcpServer();
  const deleteServer = useDeleteMcpServer();
  const [confirmModal, confirmModalContext] = useConfirmModal();

  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [authMode, setAuthMode] = useState<McpServerAuthMode>('oauth');
  const [description, setDescription] = useState('');

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!name.trim() || !url.trim() || createServer.isPending) return;
    createServer.mutate(
      { name: name.trim(), url: url.trim(), auth_mode: authMode, description: description.trim() },
      {
        onSuccess: () => {
          setName('');
          setUrl('');
          setAuthMode('oauth');
          setDescription('');
          toast.success('MCP server added — members can now connect it from Tools');
        },
        onError: (err) => toast.error(gatewayErrorMessage(err)),
      },
    );
  };

  const remove = async (server: McpServer) => {
    const confirmed = await confirmModal.error({
      title: `Remove “${server.name}”?`,
      content: "Every member's connection to it is deleted.",
      okText: 'Remove server',
      cancelText: 'Keep it',
      okButtonProps: { variant: 'destructive' },
    });
    if (!confirmed) return;
    deleteServer.mutate(server.id, {
      onSuccess: () => toast.success('MCP server removed'),
      onError: (err) => toast.error(gatewayErrorMessage(err)),
    });
  };

  // The proxy gates every /api/mcp/servers call on admin status, so a 403 here means the whole
  // panel — not just this list — has nothing an admin-only member can do. Posting a server would
  // 403 the same way, so the add form is replaced rather than left behind a dead-end button.
  if (isError && (error as Error | undefined)?.message === 'org_admin_required') {
    return (
      <div className="oa-panel mb-3 mt-3 flex min-h-0 flex-1 flex-col">
        <OrgAdminEmptyState
          icon={PlugZap}
          title="Admins only"
          description="Only org admins can manage MCP servers."
        />
      </div>
    );
  }

  return (
    <div className="oa-panel mb-3 mt-3 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-[var(--oa-gutter)]">
      <form onSubmit={submit} className="oa-raised grid gap-3 p-4 md:grid-cols-2">
        <p className="oa-meta md:col-span-2">
          Add a remote MCP server for your org. Members connect it from Tools with their own login or
          token. OAuth works only for servers that support dynamic client registration — otherwise
          choose API token.
        </p>
        <FormField label="Name" required>
          {(fieldProps) => (
            <input
              {...fieldProps}
              className={ORG_ADMIN_INPUT_CLASS}
              placeholder="Internal tools"
              value={name}
              disabled={createServer.isPending}
              onChange={(event) => setName(event.target.value)}
            />
          )}
        </FormField>
        <FormField label="Server URL" required>
          {(fieldProps) => (
            <input
              {...fieldProps}
              className={ORG_ADMIN_INPUT_CLASS}
              placeholder="https://mcp.example.com/mcp"
              value={url}
              disabled={createServer.isPending}
              onChange={(event) => setUrl(event.target.value)}
            />
          )}
        </FormField>
        <FormField label="Authentication">
          {(fieldProps) => (
            <select
              {...fieldProps}
              className={ORG_ADMIN_INPUT_CLASS}
              value={authMode}
              disabled={createServer.isPending}
              onChange={(event) => setAuthMode(event.target.value as McpServerAuthMode)}
            >
              <option value="oauth">OAuth (each member logs in)</option>
              <option value="bearer">API token (each member pastes one)</option>
            </select>
          )}
        </FormField>
        <FormField label="Description" hint="Optional">
          {(fieldProps) => (
            <input
              {...fieldProps}
              className={ORG_ADMIN_INPUT_CLASS}
              value={description}
              disabled={createServer.isPending}
              onChange={(event) => setDescription(event.target.value)}
            />
          )}
        </FormField>
        <div className="md:col-span-2">
          <Button type="submit" size="sm" disabled={createServer.isPending || !name.trim() || !url.trim()}>
            Add server
          </Button>
        </div>
      </form>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="oa-meta p-4">Loading…</div>
        ) : isError ? (
          <OrgAdminErrorState
            title="Couldn't load MCP servers"
            error={error}
            onRetry={() => void refetch()}
          />
        ) : servers.length === 0 ? (
          <OrgAdminEmptyState
            icon={PlugZap}
            title="No custom MCP servers yet"
            description="Add one above — members can connect it from Tools once it's here."
          />
        ) : (
          <div className="oa-raised divide-y divide-border/60">
            {servers.map((server) => (
              <div key={server.id} className="flex items-center justify-between gap-4 px-4 py-3">
                <div className="min-w-0">
                  <div className="oa-title">{server.name}</div>
                  <div className="oa-meta truncate">
                    {server.url} · {server.auth_mode === 'oauth' ? 'OAuth' : 'API token'}
                  </div>
                </div>
                <IconActionButton
                  label={`Remove ${server.name}`}
                  icon={Trash2}
                  tone="destructive"
                  isPending={deleteServer.isPending && deleteServer.variables === server.id}
                  onClick={() => void remove(server)}
                />
              </div>
            ))}
          </div>
        )}
      </div>
      {confirmModalContext}
    </div>
  );
}
